

import React, { useMemo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Schedule, Direction } from '../../types';
import { Shield, Clock, Layers, Box, AlertCircle, FileText, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';

const ScheduleNodeComponent = ({ data, selected }: NodeProps<Schedule>) => {
  const isEmergency = data.group === 'Emergency';
  const isActive = data.isActive;

  // Compute Effective Resources Count based on Priority Rules
  const effectiveResourceCount = useMemo(() => {
    const inherited = data._inheritedResources || [];
    const direct = data.resources;
    
    // Map to store the winner for each ResourceId
    const bestResources = new Map<string, number>();

    // 1. Process Direct Resources
    direct.forEach(r => {
        bestResources.set(r.resourceId, data.priority);
    });

    // 2. Process Inherited Resources
    inherited.forEach(r => {
        const currentBest = bestResources.get(r.resourceId);
        if (currentBest === undefined || r.sourceNodePriority > currentBest) {
            bestResources.set(r.resourceId, r.sourceNodePriority);
        }
    });

    return bestResources.size;
  }, [data.resources, data._inheritedResources, data.priority]);

  return (
    <div
      className={`relative w-[280px] shadow-sm rounded-xl border-2 transition-all duration-200 bg-white
      ${selected ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200'}
      ${!isActive ? 'opacity-70 grayscale' : ''}
      `}
    >
      <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-3 !h-3" />
      
      {/* Header */}
      <div className={`px-4 py-2 rounded-t-xl flex items-center justify-between border-b ${
        isEmergency ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'
      }`}>
        <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1
          ${isEmergency ? 'text-red-700' : 'text-slate-500'}
        `}>
          {isEmergency && <AlertCircle size={12} />}
          {data.group}
        </span>
        <div className="flex gap-2 items-center">
            {data.direction === Direction.BottomUp && (
              <span title="Inherits from Parents" className="flex items-center">
                <ArrowUpCircle size={14} className="text-slate-400" />
              </span>
            )}
            {data.direction === Direction.TopDown && (
              <span title="Inherits from Children" className="flex items-center">
                <ArrowDownCircle size={14} className="text-slate-400" />
              </span>
            )}
            <span className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-600 font-mono">
            P{data.priority}
            </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="font-semibold text-slate-800 text-sm mb-3 truncate flex items-center gap-2" title={data.name}>
          <Shield size={16} className="text-teal-600 flex-shrink-0" />
          {data.name}
        </div>

        <div className="space-y-2">
          <div className="flex items-center text-xs text-slate-600 gap-2">
            <Clock size={14} className="text-slate-400" />
            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono">{data.time}</span>
          </div>
          
          <div className="flex items-center text-xs text-slate-600 gap-2" title="Purpose">
            <FileText size={14} className="text-slate-400" />
            <span className="truncate max-w-[180px]">{data.purpose}</span>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
             <div className="flex items-center text-xs text-slate-500 gap-1.5" title="Effective Resources (After Priority Resolution)">
                <Box size={14} className="text-teal-600" />
                <span className="font-bold text-slate-700 font-mono">
                    {effectiveResourceCount}
                </span>
                <span className="text-[10px] text-slate-400 font-normal">effective</span>
             </div>
             {data.configuration?.workflow?.type && (
                <div className="flex items-center text-xs text-slate-400 gap-1" title="Workflow Type">
                    <Layers size={14} />
                    <span>Type {data.configuration.workflow.type}</span>
                </div>
             )}
          </div>
        </div>
    </div>

      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-3 !h-3" />
    </div>
  );
};

const ScheduleNode = React.memo(ScheduleNodeComponent);
export default ScheduleNode;